import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import {
  PROPOSAL_HOUR,
  atTime,
  formatLong,
  formatShort,
  formatSpan,
  fromLocalValue,
  proposals,
  sameDay,
  toLocalValue,
} from "@/lib/schedule";
import { useEffect, useMemo, useRef, useState } from "react";

const CALENDAR_WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTHS = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];
const TIME_CHIPS = ["08:00", "10:00", "14:00"];

const pad = (n: number) => String(n).padStart(2, "0");
const timeOf = (date: Date) =>
  `${pad(date.getHours())}:${pad(date.getMinutes())}`;

function parseTime(value: string): [number, number] | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours < 24 && minutes < 60 ? [hours, minutes] : null;
}

/** Days of the month view, Monday first, padded with the neighbouring months. */
function monthDays(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const count = Math.ceil((offset + last) / 7) * 7;
  return Array.from(
    { length: count },
    (_, i) => new Date(month.getFullYear(), month.getMonth(), 1 - offset + i),
  );
}

/**
 * Send time of a message: proposals relative to the maintenance start, or any
 * day and time from the calendar. `value` is "" (send at once) or a local
 * "YYYY-MM-DDTHH:MM".
 */
export function ScheduleDialog({
  value,
  start,
  onApply,
  onClose,
}: {
  value: string;
  start: Date | null;
  onApply: (value: string) => void;
  onClose: () => void;
}) {
  const now = useMemo(() => new Date(), []);
  const options = useMemo(() => (start ? proposals(start) : []), [start]);

  const initial = useMemo(() => {
    const current = fromLocalValue(value);
    if (current) {
      const match = options.find(
        (option) => option.at.getTime() === current.getTime(),
      );
      return { choice: match?.key ?? "custom", custom: current };
    }
    const next = options.find((option) => option.at > now);
    const tomorrow = atTime(
      new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1),
      PROPOSAL_HOUR,
    );
    return { choice: next?.key ?? "custom", custom: next?.at ?? tomorrow };
  }, [value, options, now]);

  const [choice, setChoice] = useState(initial.choice);
  const [customDay, setCustomDay] = useState(initial.custom);
  const [customTime, setCustomTime] = useState(timeOf(initial.custom));
  const [month, setMonth] = useState(
    new Date(initial.custom.getFullYear(), initial.custom.getMonth(), 1),
  );
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const time = parseTime(customTime);
  const selected =
    choice === "custom"
      ? time
        ? atTime(customDay, time[0], time[1])
        : null
      : (options.find((option) => option.key === choice)?.at ?? null);
  const inPast = selected !== null && selected <= now;
  const afterStart = selected !== null && start !== null && selected >= start;

  const summary = (() => {
    if (!selected) return "Uhrzeit als HH:MM eingeben";
    if (inPast) return "Liegt in der Vergangenheit";
    const parts = [
      sameDay(selected, now)
        ? `Heute in ${formatSpan(selected.getTime() - now.getTime())}`
        : `In ${formatSpan(selected.getTime() - now.getTime())}`,
    ];
    if (start) {
      parts.push(
        afterStart
          ? `${formatSpan(selected.getTime() - start.getTime())} nach Wartungsbeginn`
          : `${formatSpan(start.getTime() - selected.getTime())} vor Wartungsbeginn`,
      );
    }
    return parts.join(" · ");
  })();

  const pickDay = (day: Date) => {
    setChoice("custom");
    setCustomDay(day);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-title"
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
        className="w-full max-w-md rounded-lg border border-hairline bg-surface shadow-xl outline-none"
      >
        <div className="border-b border-hairline px-5 pt-4 pb-3">
          <h2 id="schedule-title" className="text-sm font-semibold">
            Wann soll die Mail raus?
          </h2>
          {start ? (
            <p className="mt-0.5 text-xs text-muted">
              Wartungsbeginn: {formatLong(start)}
            </p>
          ) : null}
        </div>

        <fieldset className="min-w-0 space-y-1.5 px-5 py-3">
          <legend className="sr-only">Versandtermin</legend>
          {options.map((option) => {
            const past = option.at <= now;
            return (
              <label
                key={option.key}
                className={cn(
                  "flex items-start gap-2.5 rounded-md border px-3 py-2",
                  choice === option.key
                    ? "border-accent bg-accent-dim"
                    : "border-hairline",
                  past ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                )}
              >
                <input
                  type="radio"
                  name="schedule-choice"
                  className="mt-0.5"
                  disabled={past}
                  checked={choice === option.key}
                  onChange={() => setChoice(option.key)}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex justify-between gap-2">
                    <span
                      className={cn(
                        choice === option.key ? "font-semibold" : "font-medium",
                      )}
                    >
                      {option.label}
                    </span>
                    <span className="font-mono text-xs text-muted">
                      {past ? "vorbei" : formatShort(option.at)}
                    </span>
                  </span>
                  {option.note && !past ? (
                    <span className="block text-[11px] text-amber">
                      {option.note}
                    </span>
                  ) : null}
                </span>
              </label>
            );
          })}

          <div
            className={cn(
              "space-y-3 rounded-md border px-3 py-2",
              choice === "custom"
                ? "border-accent bg-accent-dim"
                : "border-hairline",
            )}
          >
            <label className="flex cursor-pointer items-center gap-2.5">
              <input
                type="radio"
                name="schedule-choice"
                checked={choice === "custom"}
                onChange={() => setChoice("custom")}
              />
              <span className="flex flex-1 justify-between gap-2">
                <span
                  className={cn(
                    choice === "custom" ? "font-semibold" : "font-medium",
                  )}
                >
                  {options.length ? "Anderer Termin" : "Termin"}
                </span>
                {choice === "custom" && selected ? (
                  <span className="font-mono text-xs font-medium text-accent">
                    {formatShort(selected)}
                  </span>
                ) : null}
              </span>
            </label>

            {choice === "custom" ? (
              <div className="flex gap-4 pl-6">
                <div className="w-56 shrink-0 rounded-md border border-hairline bg-surface p-2">
                  <div className="mb-1 flex items-center justify-between">
                    <button
                      type="button"
                      aria-label="Voriger Monat"
                      className="flex h-6 w-6 items-center justify-center rounded-md border border-hairline text-muted hover:bg-surface-subtle"
                      onClick={() =>
                        setMonth(
                          new Date(
                            month.getFullYear(),
                            month.getMonth() - 1,
                            1,
                          ),
                        )
                      }
                    >
                      ‹
                    </button>
                    <span className="text-xs font-semibold">
                      {MONTHS[month.getMonth()]} {month.getFullYear()}
                    </span>
                    <button
                      type="button"
                      aria-label="Nächster Monat"
                      className="flex h-6 w-6 items-center justify-center rounded-md border border-hairline text-muted hover:bg-surface-subtle"
                      onClick={() =>
                        setMonth(
                          new Date(
                            month.getFullYear(),
                            month.getMonth() + 1,
                            1,
                          ),
                        )
                      }
                    >
                      ›
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-0.5 text-center">
                    {CALENDAR_WEEKDAYS.map((day) => (
                      <span
                        key={day}
                        className="py-0.5 text-[10px] font-medium text-muted"
                      >
                        {day}
                      </span>
                    ))}
                    {monthDays(month).map((day) => {
                      const past =
                        day <
                        new Date(
                          now.getFullYear(),
                          now.getMonth(),
                          now.getDate(),
                        );
                      const picked = sameDay(day, customDay);
                      const maintenance = start !== null && sameDay(day, start);
                      return (
                        <button
                          key={day.toISOString()}
                          type="button"
                          disabled={past}
                          aria-pressed={picked}
                          aria-label={`${day.getDate()}. ${MONTHS[day.getMonth()]}${maintenance ? ", Wartung" : ""}`}
                          onClick={() => pickDay(day)}
                          className={cn(
                            "h-6 rounded text-[11px] tabular-nums",
                            picked
                              ? "border border-accent bg-accent font-semibold text-accent-ink"
                              : maintenance
                                ? "border border-amber bg-[color-mix(in_srgb,var(--color-amber)_15%,transparent)] font-semibold"
                                : "border border-transparent hover:bg-surface-subtle",
                            sameDay(day, now) && "ring-1 ring-inset ring-ink",
                            (past || day.getMonth() !== month.getMonth()) &&
                              !picked &&
                              "text-muted opacity-60",
                            past && "cursor-not-allowed",
                          )}
                        >
                          {day.getDate()}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-1.5 flex gap-2.5 text-[10px] text-muted">
                    <span className="inline-flex items-center gap-1">
                      <span className="h-2 w-2 rounded-sm bg-accent" />
                      Versand
                    </span>
                    {start ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="h-2 w-2 rounded-sm border border-amber bg-[color-mix(in_srgb,var(--color-amber)_15%,transparent)]" />
                        Wartung
                      </span>
                    ) : null}
                    <span className="inline-flex items-center gap-1">
                      <span className="h-2 w-2 rounded-sm ring-1 ring-inset ring-ink" />
                      Heute
                    </span>
                  </div>
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <span className="text-[11px] font-medium text-muted">
                    Uhrzeit
                  </span>
                  {TIME_CHIPS.map((chip) => (
                    <button
                      key={chip}
                      type="button"
                      aria-pressed={customTime === chip}
                      onClick={() => setCustomTime(chip)}
                      className={cn(
                        "rounded-md border py-1 font-mono text-xs",
                        customTime === chip
                          ? "border-accent bg-accent-dim font-medium text-accent"
                          : "border-hairline bg-surface hover:bg-surface-subtle",
                      )}
                    >
                      {chip}
                    </button>
                  ))}
                  <label
                    className="field-label mt-1 mb-0"
                    htmlFor="schedule-time"
                  >
                    oder genau
                  </label>
                  <input
                    id="schedule-time"
                    className={cn(
                      "field font-mono text-xs",
                      !time && "border-danger",
                    )}
                    value={customTime}
                    placeholder="HH:MM"
                    onChange={(e) => setCustomTime(e.target.value)}
                  />
                </div>
              </div>
            ) : null}
          </div>
          <p
            className={cn(
              "px-1 pt-1 text-xs",
              inPast || afterStart || !selected ? "text-amber" : "text-muted",
            )}
          >
            {summary}
          </p>
        </fieldset>

        <div className="flex items-center gap-2 border-t border-hairline bg-surface-subtle px-5 py-3">
          <button
            type="button"
            className="mr-auto text-xs font-medium text-accent underline underline-offset-2"
            onClick={() => onApply("")}
          >
            Doch sofort senden
          </button>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
            disabled={!selected || inPast}
            onClick={() => selected && onApply(toLocalValue(selected))}
          >
            Übernehmen
          </Button>
        </div>
      </div>
    </div>
  );
}
